import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * <preset-pack-builder>
 *
 * Curated 2-Pack and 3-Pack.
 *
 * The shopper still picks a combo and size from the theme's own variant picker
 * on the pack's own product page. What this changes is what reaches the cart:
 * instead of a pack variant carrying its own stock, it adds the real
 * single-trunk variants the combo is made of, so every sale draws down the same
 * 35 base SKUs the BYO packs and loose singles already use.
 *
 * Price comes from the pack-pricing discount function, which charges the
 * cheapest combination of pack prices for whatever is in the cart. There is
 * deliberately no price logic here.
 *
 * The combo -> colours split happens in Liquid, not here, so the combo naming
 * can't drift out of step with what the shopper is actually sent.
 *
 * If this fails the native form still posts the pack variant, so a sale is
 * never silently lost — it just doesn't draw down the singles.
 */
class PresetPackBuilder extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#detachSubmit();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#init();
  }

  #init() {
    this.singles = this.#readJson('script[data-singles]');
    this.packs = this.#readJson('script[data-packs]');
    this.#attachSubmit();
  }

  #readJson(selector) {
    const node = this.querySelector(selector);
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[preset-pack]', selector, error);
      return {};
    }
  }

  get #form() {
    const scope = this.closest('.shopify-section') || document;
    return scope.querySelector('form[action*="/cart/add"]');
  }

  #attachSubmit() {
    this.#detachSubmit();
    const form = this.#form;
    if (!form) return;
    this.boundForm = form;

    // The theme's add-to-cart button runs its own click handler rather than
    // submitting the form, so intercept the click on the document in capture
    // phase — that runs before any handler bound further down the tree. The
    // submit listener is a belt-and-braces fallback.
    document.addEventListener('click', this.#onAddToCartClick, true);
    form.addEventListener('submit', this.#onSubmit, true);
  }

  #detachSubmit() {
    document.removeEventListener('click', this.#onAddToCartClick, true);
    this.boundForm?.removeEventListener('submit', this.#onSubmit, true);
    this.boundForm = null;
  }

  #onAddToCartClick = (event) => {
    const button = event.target.closest?.(
      '[ref="addToCartButton"], .add-to-cart-button, button[type="submit"], button[name="add"]'
    );
    if (!button || !this.boundForm?.contains(button)) return;
    this.#onSubmit(event);
  };

  /** The pack variant the picker currently has selected. */
  get #selectedPackId() {
    const input = this.boundForm?.querySelector('input[name="id"], select[name="id"]');
    return input?.value ?? '';
  }

  /** How many packs the shopper asked for. */
  get #packQuantity() {
    const input = this.boundForm?.querySelector('input[name="quantity"], select[name="quantity"]');
    const n = parseInt(input?.value ?? '1', 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  #setError(message) {
    const el = this.querySelector('.preset-pack-builder__error');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
    this.hidden = !message;
  }

  #onSubmit = async (event) => {
    const pack = this.packs[this.#selectedPackId];
    // Unknown variant — let the native form handle it rather than block a sale.
    if (!pack || !pack.colours?.length) return;

    const packCount = this.#packQuantity;
    const items = [];
    const unavailable = [];

    for (let n = 0; n < packCount; n++) {
      // A fresh id per pack, so two of the same pack stay separate groups in
      // the cart rather than merging into one another.
      const packId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

      for (const colour of pack.colours) {
        const entry = this.singles[`${pack.size}|${colour}`];
        if (!entry || !entry.available) {
          unavailable.push(`${colour} / ${pack.size}`);
          continue;
        }
        items.push({
          id: entry.id,
          quantity: 1,
          properties: {
            // Underscore-prefixed: hidden from cart and checkout, but still on
            // the order in admin for the warehouse to pack against.
            _Pack: `${this.dataset.packLabel} — ${pack.combo}`,
            _PackId: packId,
          },
        });
      }
    }

    if (!items.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (unavailable.length) {
      this.#setError(`Sold out: ${[...new Set(unavailable)].join(', ')}`);
      return;
    }

    const button = this.boundForm?.querySelector('[ref="addToCartButton"], button[type="submit"]');
    button?.setAttribute('disabled', '');

    try {
      const response = await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || body.message || 'Could not add to cart');
      }

      this.#setError('');
      document.dispatchEvent(new CartAddEvent({}, String(items[0].id), { didError: false }));
      document.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate, { bubbles: true, detail: { data: {} } }));
    } catch (error) {
      console.error('[preset-pack]', error);
      this.#setError(error.message || 'Could not add to cart');
    } finally {
      button?.removeAttribute('disabled');
    }
  };
}

if (!customElements.get('preset-pack-builder')) {
  customElements.define('preset-pack-builder', PresetPackBuilder);
}
